import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CircularProgress from '@mui/material/CircularProgress';

interface AutoSaveIndicatorProps {
  isSaving: boolean;
  lastSaved: Date | null;
  hasError?: boolean;
}

const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.floor(diffMin / 60);
  return `${diffHour}h ago`;
};

export const AutoSaveIndicator: React.FC<AutoSaveIndicatorProps> = ({
  isSaving,
  lastSaved,
  hasError = false,
}) => {
  if (isSaving) {
    return (
      <Chip
        size="small"
        icon={<CircularProgress size={12} color="inherit" />}
        label="Saving..."
        color="default"
        variant="outlined"
        sx={{ fontSize: '0.75rem' }}
      />
    );
  }

  if (hasError) {
    return (
      <Chip
        size="small"
        icon={<ErrorIcon fontSize="small" />}
        label="Error saving"
        color="error"
        variant="outlined"
        sx={{ fontSize: '0.75rem' }}
      />
    );
  }

  if (lastSaved) {
    const relTime = formatRelativeTime(lastSaved);
    const fullTime = lastSaved.toLocaleTimeString();
    return (
      <Tooltip title={`Last saved at ${fullTime}`}>
        <Chip
          size="small"
          icon={<CheckCircleIcon fontSize="small" />}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption">Saved</Typography>
              <Typography variant="caption" color="text.secondary">
                {relTime}
              </Typography>
            </Box>
          }
          color="success"
          variant="outlined"
          sx={{ fontSize: '0.75rem' }}
        />
      </Tooltip>
    );
  }

  return (
    <Chip
      size="small"
      icon={<SaveIcon fontSize="small" />}
      label="Not saved"
      color="default"
      variant="outlined"
      sx={{ fontSize: '0.75rem' }}
    />
  );
};
